/**
 * Analytics Event Tracking
 * Type-safe event tracking for GA4
 */

import { gtmManager, DataLayerEvent } from './gtm';

/**
 * Standard GA4 Event Parameters
 */
export interface BaseEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  user_id?: string;
  [key: string]: unknown;
}

/**
 * Page View Event
 */
export interface PageViewEvent extends BaseEventParams {
  page_title: string;
  page_location: string;
  page_path: string;
}

/**
 * User Interaction Events
 */
export interface InteractionEvent extends BaseEventParams {
  interaction_type: string;
  element_id?: string;
  element_text?: string;
}

/**
 * E-commerce Events
 */
export interface EcommerceEvent extends BaseEventParams {
  currency?: string;
  value?: number;
  items?: Array<{
    item_id: string;
    item_name: string;
    price?: number;
    quantity?: number;
  }>;
}

class AnalyticsTracker {
  /**
   * Track page view
   */
  trackPageView(params: PageViewEvent): void {
    const event: DataLayerEvent = {
      event: 'page_view',
      page_title: params.page_title,
      page_location: params.page_location,
      page_path: params.page_path,
      ...params,
    };
    gtmManager.pushEvent(event);
  }

  /**
   * Track custom event
   */
  trackEvent(eventName: string, params: BaseEventParams = {}): void {
    const event: DataLayerEvent = {
      event: eventName,
      ...params,
    };
    gtmManager.pushEvent(event);
  }

  /**
   * Track user interaction
   */
  trackInteraction(params: InteractionEvent): void {
    const event: DataLayerEvent = {
      event: 'user_interaction',
      ...params,
    };
    gtmManager.pushEvent(event);
  }

  /**
   * Track button click
   */
  trackButtonClick(buttonName: string, additionalParams: BaseEventParams = {}): void {
    this.trackEvent('button_click', {
      event_category: 'engagement',
      event_label: buttonName,
      ...additionalParams,
    });
  }

  /**
   * Track form submission
   */
  trackFormSubmit(formName: string, additionalParams: BaseEventParams = {}): void {
    this.trackEvent('form_submit', {
      event_category: 'engagement',
      event_label: formName,
      ...additionalParams,
    });
  }

  /**
   * Track sign up
   */
  trackSignUp(method: string = 'email'): void {
    this.trackEvent('sign_up', {
      event_category: 'user',
      method,
    });
  }

  /**
   * Track login
   */
  trackLogin(method: string = 'email'): void {
    this.trackEvent('login', {
      event_category: 'user',
      method,
    });
  }

  /**
   * Track logout
   */
  trackLogout(): void {
    this.trackEvent('logout', {
      event_category: 'user',
    });
  }

  /**
   * Track search
   */
  trackSearch(searchTerm: string): void {
    this.trackEvent('search', {
      event_category: 'engagement',
      search_term: searchTerm,
    });
  }

  /**
   * Track share
   */
  trackShare(contentType: string, method: string): void {
    this.trackEvent('share', {
      event_category: 'engagement',
      content_type: contentType,
      method,
    });
  }

  /**
   * Track error
   */
  trackError(errorMessage: string, errorLocation?: string): void {
    this.trackEvent('error', {
      event_category: 'error',
      error_message: errorMessage,
      error_location: errorLocation,
    });
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, unknown>): void {
    const event: DataLayerEvent = {
      event: 'user_properties',
      ...properties,
    };
    gtmManager.pushEvent(event);
  }

  /**
   * Track e-commerce purchase
   */
  trackPurchase(params: EcommerceEvent): void {
    const event: DataLayerEvent = {
      event: 'purchase',
      ...params,
    };
    gtmManager.pushEvent(event);
  }

  /**
   * Track add to cart
   */
  trackAddToCart(params: EcommerceEvent): void {
    const event: DataLayerEvent = {
      event: 'add_to_cart',
      ...params,
    };
    gtmManager.pushEvent(event);
  }
}

export const analytics = new AnalyticsTracker();
